/**
 * Pure mutation functions for miscellaneous actions.
 */

import {
    applyActions,
    ensureRouteVertexState,
    getDefaultValueProperty,
    getProperty,
    PcbTreeManager,
    removeNodesAndDescendants,
    setElementsAndGenerateNodes,
    updateSelectedSubjects,
} from "@buildwithflux/core";
import type {ApplyActionsResult, EnrichedCopilotActions} from "@buildwithflux/core";
import type {
    AnyPcbNode,
    IDocumentData,
    IElementData,
    IPropertiesMap,
    IRouteData,
    ISubDocumentData,
    IPartVersionData,
    IVector3,
    MetamoduleAttachmentSides,
    PcbNodesMap,
    UserCodeData,
    UserUid,
    VertexUpdate,
} from "@buildwithflux/models";
import {applyChange} from "deep-diff";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import {DocumentStorageHelper} from "modules/storage_engine/helpers/DocumentStorageHelper";

export function applySetBelongsToPartUid(draft: IDocumentData, partUid: string): void {
    draft.belongs_to_part_uid = partUid;
}

export function applySetRulesetUserData(draft: IDocumentData, rulesetUid: string, userData: UserCodeData): void {
    const draftRuleset = draft.pcbLayoutRuleSets[rulesetUid];
    if (draftRuleset) {
        draftRuleset.userCodeData = userData;
    }
}

export function applySetElementUserData(draft: IDocumentData, elementUid: string, userData: UserCodeData): void {
    const draftElement = draft.elements[elementUid];
    if (draftElement) {
        draftElement.userCodeData = userData;
    }
}

export function applySetAssetUserData(draft: IDocumentData, assetUid: string, userData: UserCodeData): void {
    const draftAsset = draft.assets[assetUid];
    if (draftAsset) {
        draftAsset.userCodeData = userData;
    }
}

export function applySetControlUserData(draft: IDocumentData, controlUid: string, userData: UserCodeData): void {
    if (!draft.controls) {
        draft.controls = [];
    }
    const controlData = draft.controls.find((control) => control.uid === controlUid);
    if (controlData) {
        controlData.userCodeData = userData;
    }
}

export function applyUpdateRouteProperties(draft: IDocumentData, routeUid: string, properties: IPropertiesMap): void {
    const draftRoute = draft.routes[routeUid];
    if (draftRoute) {
        draftRoute.properties = properties;
    }
}

export function applyUpdateRoutesLabel(draft: IDocumentData, routes: IRouteData[]): void {
    const oldRoutes = draft.routes;
    for (const updatedRoute of routes) {
        const oldRoute = oldRoutes[updatedRoute.uid];
        if (oldRoute) {
            oldRoute.label = updatedRoute.label;
            oldRoute.updated_at = new Date().getTime();
        }
    }
}

export function applyUpdateWiring(
    draft: IDocumentData,
    vertexUpdates: VertexUpdate[] | undefined,
    selectedObjectUids: string[] | undefined,
): void {
    for (const update of vertexUpdates ?? []) {
        applyChange(draft.routeVertices, true, update);
    }
    ensureRouteVertexState(draft);

    const pcbTreeManager = new PcbTreeManager(draft.pcbLayoutNodes, FluxLogger);
    const {selectionUpdates} = pcbTreeManager.updateNetNodes({...draft});

    if (selectedObjectUids) {
        const selections = new Set<string>(selectedObjectUids.concat(selectionUpdates));
        updateSelectedSubjects(draft, Array.from(selections.values()));
    }

    draft.pcbLayoutNodes = pcbTreeManager.treeNodes as PcbNodesMap<AnyPcbNode>;
}

export function applyConvertSubjectsToNewPart(
    draft: IDocumentData,
    originalElements: IElementData[],
    newElement: IElementData,
    vertexUpdates: VertexUpdate[] | undefined,
): void {
    DocumentStorageHelper.deleteElements(draft, originalElements);
    const oldElementNodes = originalElements.map((el) => el.uid);
    removeNodesAndDescendants(draft.pcbLayoutNodes, oldElementNodes);
    setElementsAndGenerateNodes(draft, [newElement]);
    for (const update of vertexUpdates ?? []) {
        applyChange(draft.routeVertices, true, update);
    }
}

export function applyCreateLocalPart(
    draft: IDocumentData,
    originalElement: IElementData,
    newElement: IElementData,
): void {
    DocumentStorageHelper.deleteElements(draft, [originalElement]);
    removeNodesAndDescendants(draft.pcbLayoutNodes, [originalElement.uid]);
    setElementsAndGenerateNodes(draft, [newElement]);
}

export function applyReplaceElementPartVersionDataCache(draft: IDocumentData, elements: IElementData[]): void {
    const oldElements = draft.elements;
    for (const updatedElementData of elements) {
        const oldElement = oldElements[updatedElementData.uid];
        if (!oldElement) {
            continue;
        }
        oldElement.part_uid = updatedElementData.part_uid;
        oldElement.part_version = updatedElementData.part_version;
        oldElement.part_version_data_cache = updatedElementData.part_version_data_cache;
        oldElement.updated_at = new Date().getTime();
    }
    setElementsAndGenerateNodes(draft, elements);
    const pcbTreeManager = new PcbTreeManager(draft.pcbLayoutNodes, FluxLogger);
    pcbTreeManager.updateNetNodes(draft);
    draft.pcbLayoutNodes = pcbTreeManager.treeNodes as PcbNodesMap<AnyPcbNode>;
}

export function applyMergeSubDocumentData(draft: IDocumentData, subDocumentDatas: ISubDocumentData[]): void {
    const subDocumentData = draft.subDocumentData ?? {elements: {}, routeVertices: {}};
    for (const subDocumentDataUpdate of subDocumentDatas) {
        for (const [collectionName, collection] of Object.entries(subDocumentDataUpdate)) {
            Object.assign(subDocumentData[collectionName as keyof ISubDocumentData], collection);
        }
    }
    draft.subDocumentData = subDocumentData;
    const pcbDomTreeManager = new PcbTreeManager(draft.pcbLayoutNodes, FluxLogger);
    pcbDomTreeManager.updateNetNodes(draft);
}

export function applyDeleteSubDocumentData(draft: IDocumentData, elementUids: string[]): void {
    const {subDocumentData} = draft;
    if (!subDocumentData) {
        return;
    }
    for (const elementUid of elementUids) {
        for (const collection of Object.values(subDocumentData)) {
            for (const nestedUid of Object.keys(collection)) {
                if (nestedUid.startsWith(elementUid)) {
                    delete collection[nestedUid];
                }
            }
        }
    }
    const pcbDomTreeManager = new PcbTreeManager(draft.pcbLayoutNodes, FluxLogger);
    pcbDomTreeManager.updateNetNodes(draft);
}

export function applyStartReceivingLatestDraftsForPartUid(
    draft: IDocumentData,
    partUid: string,
    currentPartVersion: string,
): void {
    const newEntry = {[partUid]: {revertToVersion: currentPartVersion}};
    if (!draft.receiveLatestDraftsForPartUids) {
        draft.receiveLatestDraftsForPartUids = newEntry;
    } else {
        draft.receiveLatestDraftsForPartUids = {
            ...draft.receiveLatestDraftsForPartUids,
            ...newEntry,
        };
    }
}

export function applyStopReceivingLatestDraftsForPartUid(draft: IDocumentData, partUid: string): void {
    if (draft.receiveLatestDraftsForPartUids) {
        delete draft.receiveLatestDraftsForPartUids[partUid];
    }
}

export function applyUpdatePlotsPosition(
    draft: IDocumentData,
    elementId: string,
    position: IVector3,
    attached: MetamoduleAttachmentSides | null,
): void {
    const draftElement = draft.elements[elementId];
    if (!draftElement) {
        return;
    }
    const metamodule = draftElement.metamodule;
    if (metamodule) {
        if (metamodule.attached !== attached) {
            metamodule.attached = attached;
        }
        if (
            metamodule.position?.x !== position.x ||
            metamodule.position?.y !== position.y ||
            metamodule.position?.z !== position.z
        ) {
            metamodule.position = position;
        }
    } else {
        draftElement.metamodule = {
            position,
            attached,
            visibilityToggleMap: {},
        };
    }
}

export function applyUpdatePropertyVisibility(
    draft: IDocumentData,
    elementIds: string[],
    propertyId: string,
    visibility: boolean,
): void {
    let resolvedPropertyId = propertyId;
    for (const elementId of elementIds) {
        const element = draft.elements[elementId];
        if (!element) continue;

        const metamodule = element.metamodule;
        let property = element.properties[resolvedPropertyId];

        if (!property) {
            property = getProperty(resolvedPropertyId, element.properties);
            if (property) {
                resolvedPropertyId = property.uid;
            }
        }

        if (metamodule) {
            metamodule.visibilityToggleMap[resolvedPropertyId] = visibility;
        } else {
            const isDefaultProperty = getDefaultValueProperty(element.properties)?.uid === resolvedPropertyId;
            element.metamodule = {
                position: undefined,
                attached: null,
                visibilityToggleMap: {[resolvedPropertyId]: isDefaultProperty ? false : true},
            };
        }
    }
}

export function applyApplyCopilotActions(
    draft: IDocumentData,
    actions: EnrichedCopilotActions,
    portalPart: IPartVersionData,
    groundPart: IPartVersionData,
    userUid: UserUid,
    resultCallback: (result: ApplyActionsResult) => void,
): void {
    const actionsResult = applyActions({
        actions,
        document: draft,
        actionContext: {
            portalPart,
            groundPart,
            userUid,
            logger: FluxLogger,
        },
    });
    resultCallback(actionsResult);
}
